import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSession } from '../context/SessionContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaOrcid, FaEye, FaEyeSlash } from 'react-icons/fa';
import SafeLink from './SafeLink';
import CountrySelector from './common/CountrySelector';
import './Signup.css';

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const { sessionRole } = useSession();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    country: '',
    roleName: 'INTERESTED',
    terms: false,
    confirmPassword: '',
    // Investigator fields
    affiliation: '',
    orcid: '',
    clinicalStudyParticipant: 'no',
    // Patient/Interested fields
    diseaseOfInterest: '',
    userType: 'patient',
    // Institution fields
    institutionName: '',
    institutionType: 'university',
    website: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Validación de email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Lista de dominios de correo común
  const commonEmailDomains = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
    'aol.com', 'protonmail.com', 'mail.com', 'icloud.com',
    'mail.ru', 'yandex.com', 'rambler.ru', 'zoho.com', 'gmx.com'
  ];

  // Validación de email institucional
  const isInstitutionalEmail = (email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    return domain && !commonEmailDomains.includes(domain);
  };

  // Validación de contraseña
  const isStrongPassword = (password) => {
    if (password.length < 8) return false;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/.test(password);
    return hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
  };

  // Validación de campos requeridos por rol
  const validateFormFields = () => {
    const newErrors = {};

    // Campos comunes
    if (!formData.firstName.trim()) newErrors.firstName = t('signup.validation.fieldRequired');
    if (!formData.lastName.trim()) newErrors.lastName = t('signup.validation.fieldRequired');
    if (!formData.country.trim()) newErrors.country = t('signup.validation.countryRequired');

    // Email
    if (!formData.email.trim()) {
      newErrors.email = t('signup.validation.fieldRequired');
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = t('signup.validation.invalidEmail');
    } else if ((formData.roleName === 'INVESTIGATOR' || formData.roleName === 'INSTITUTION') && !isInstitutionalEmail(formData.email)) {
      newErrors.email = t('signup.validation.commonEmailDomains');
    }

    // Contraseña
    if (!formData.password.trim()) {
      newErrors.password = t('signup.validation.fieldRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('signup.validation.passwordTooShort');
    } else if (!isStrongPassword(formData.password)) {
      newErrors.password = t('signup.validation.passwordWeak');
    }

    // Confirmación de contraseña
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = t('signup.validation.fieldRequired');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('signup.passwordsDontMatch');
    }

    // Campos específicos por rol
    if (formData.roleName === 'INVESTIGATOR') {
      if (!formData.affiliation.trim()) {
        newErrors.affiliation = t('signup.validation.invalidAffiliation');
      }
    } else if (formData.roleName === 'INTERESTED' || formData.roleName === 'PATIENT') {
      if (!formData.diseaseOfInterest.trim()) {
        newErrors.diseaseOfInterest = t('signup.validation.invalidDiseaseOfInterest');
      }
    } else if (formData.roleName === 'INSTITUTION') {
      if (!formData.institutionName.trim()) {
        newErrors.institutionName = t('signup.validation.invalidInstitutionName');
      }
    }

    // Términos
    if (!formData.terms) {
      newErrors.terms = t('signup.validation.affirmTerms');
    }

    return newErrors;
  };

  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    // Prioridad 1: SessionContext (desde Landing/Login)
    if (sessionRole) {
      const roleUpper = sessionRole.toUpperCase();
      console.log('Signup - Setting role from SessionContext:', roleUpper);
      setFormData(prev => ({ ...prev, roleName: roleUpper }));
      return;
    }

    // Prioridad 2: URL params (fallback)
    const params = new URLSearchParams(location.search);
    const role = params.get('role');
    console.log('URL Search Params:', location.search);
    console.log('Role from URL:', role);
    if (role) {
      const roleUpper = role.toUpperCase();
      console.log('Setting role to:', roleUpper);
      setFormData(prev => ({ ...prev, roleName: roleUpper }));
    }
  }, [location, sessionRole]);

  useEffect(() => {
    console.log("Form Data Role changed to:", formData.roleName);
  }, [formData.roleName]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    
    // Validar todos los campos
    const errors = validateFormFields();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Mostrar el primer error general
      const firstError = Object.values(errors)[0];
      setError(firstError);
      return;
    }

    setFieldErrors({});
    setLoading(true);
    setError('');

    const dataToRegister = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      roleName: formData.roleName,
      country: formData.country,
      affiliation: formData.affiliation,
      orcid: formData.orcid,
      clinicalStudyParticipant: formData.clinicalStudyParticipant === 'yes',
      diseaseOfInterest: formData.diseaseOfInterest,
      userType: formData.userType,
      institutionName: formData.institutionName,
      institutionType: formData.institutionType,
      website: formData.website,
    };

    console.log('Submitting registration with data:', dataToRegister);

    const res = await register(dataToRegister);

    if (res.success) {
      // Redirect based on role
      if (formData.roleName === 'INVESTIGATOR' || formData.roleName === 'INSTITUTION') {
        navigate('/feed', { state: { from: 'internal' } });
      } else if (formData.roleName === 'INTERESTED' || formData.roleName === 'PATIENT') {
        navigate('/community-page', { state: { from: 'internal' } });
      } else {
        navigate('/profile', { state: { from: 'internal' } });
      }
    } else {
      setError(res.message);
    }
    setLoading(false);
  };

  return (
    <div className="signup-page">
      <div className="signup-left">
        <div className="signup-left-content">
          <img src="/logoecseia.png" alt="Logo" className="signup-logo-icon" />
          <h1 className="signup-brand-name">
            <span className="brand-eco">Eco</span>ScienceInnova
          </h1>
          <p className="signup-tagline">{t('tagline')}</p>
        </div>
        <p className="signup-left-footer">© 2026 EcoScienceInnova Labs</p>
      </div>

      <div className="signup-right">
        <div className="signup-card">
          <h2 className="signup-title">{t('auth.registerTitle')}</h2>
          <p className="signup-subtitle">{t('signup.subtitle')}</p>

          {error && <div className="login-error">{error}</div>}

          <form className="signup-form" onSubmit={onSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">{t('signup.firstName')}</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className={`signup-input ${fieldErrors.firstName ? 'input-error' : ''}`}
                  value={formData.firstName}
                  onChange={onChange}
                  required
                />
                {fieldErrors.firstName && <span className="error-text">{fieldErrors.firstName}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="lastName">{t('signup.lastName')}</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className={`signup-input ${fieldErrors.lastName ? 'input-error' : ''}`}
                  value={formData.lastName}
                  onChange={onChange}
                  required
                />
                {fieldErrors.lastName && <span className="error-text">{fieldErrors.lastName}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">{t('signup.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                className={`signup-input ${fieldErrors.email ? 'input-error' : ''}`}
                value={formData.email}
                onChange={onChange}
                required
              />
              {fieldErrors.email && <span className="error-text">{fieldErrors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="country">{t('signup.country')}</label>
              <CountrySelector
                value={formData.country}
                onChange={onChange}
              />
              {fieldErrors.country && <span className="error-text">{fieldErrors.country}</span>}
            </div>

            {formData.roleName === 'INVESTIGATOR' && (
              <>
                <div className="form-group">
                  <label htmlFor="affiliation">{t('signup.affiliation')}</label>
                  <input
                    type="text"
                    id="affiliation"
                    name="affiliation"
                    className={`signup-input ${fieldErrors.affiliation ? 'input-error' : ''}`}
                    value={formData.affiliation}
                    onChange={onChange}
                    placeholder={t('signup.affiliationPlaceholder')}
                    required
                  />
                  {fieldErrors.affiliation && <span className="error-text">{fieldErrors.affiliation}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="orcid">{t('signup.orcid')}</label>
                  <input
                    type="text"
                    id="orcid"
                    name="orcid"
                    className="signup-input"
                    value={formData.orcid}
                    onChange={onChange}
                    placeholder={t('signup.orcidPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('signup.clinicalStudyParticipant')}</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="clinicalStudyParticipant"
                        value="yes"
                        checked={formData.clinicalStudyParticipant === 'yes'}
                        onChange={onChange}
                      />
                      {t('yes')}
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="clinicalStudyParticipant"
                        value="no"
                        checked={formData.clinicalStudyParticipant === 'no'}
                        onChange={onChange}
                      />
                      {t('no')}
                    </label>
                  </div>
                </div>
              </>
            )}

            {['INTERESTED', 'PATIENT'].includes(formData.roleName) && (
              <>
                <div className="form-group">
                  <label htmlFor="diseaseOfInterest">{t('signup.diseaseOfInterest')}</label>
                  <input
                    type="text"
                    id="diseaseOfInterest"
                    name="diseaseOfInterest"
                    className={`signup-input ${fieldErrors.diseaseOfInterest ? 'input-error' : ''}`}
                    value={formData.diseaseOfInterest}
                    onChange={onChange}
                    placeholder={t('signup.diseaseOfInterestPlaceholder')}
                    required
                  />
                  {fieldErrors.diseaseOfInterest && <span className="error-text">{fieldErrors.diseaseOfInterest}</span>}
                </div>
                <div className="form-group">
                  <label>{t('signup.userType')}</label>
                  <select
                    name="userType"
                    className="signup-input"
                    value={formData.userType}
                    onChange={onChange}
                  >
                    <option value="patient">{t('signup.userTypeOptions.patient')}</option>
                    <option value="family_member">{t('signup.userTypeOptions.familyMember')}</option>
                    <option value="caregiver">{t('signup.userTypeOptions.caregiver')}</option>
                  </select>
                </div>
              </>
            )}

            {formData.roleName === 'INSTITUTION' && (
              <>
                <div className="form-group">
                  <label htmlFor="institutionName">{t('signup.institutionName')}</label>
                  <input
                    type="text"
                    id="institutionName"
                    name="institutionName"
                    className={`signup-input ${fieldErrors.institutionName ? 'input-error' : ''}`}
                    value={formData.institutionName}
                    onChange={onChange}
                    placeholder={t('signup.institutionNamePlaceholder')}
                    required
                  />
                  {fieldErrors.institutionName && <span className="error-text">{fieldErrors.institutionName}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="institutionType">{t('signup.institutionType')}</label>
                  <select
                    id="institutionType"
                    name="institutionType"
                    className="signup-input"
                    value={formData.institutionType}
                    onChange={onChange}
                    required
                  >
                    <option value="university">{t('signup.institutionTypeOptions.university')}</option>
                    <option value="hospital">{t('signup.institutionTypeOptions.hospital')}</option>
                    <option value="research_center">{t('signup.institutionTypeOptions.researchCenter')}</option>
                    <option value="pharmaceutical">{t('signup.institutionTypeOptions.pharmaceutical')}</option>
                    <option value="other">{t('signup.institutionTypeOptions.other')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="website">{t('signup.website')}</label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    className="signup-input"
                    value={formData.website}
                    onChange={onChange}
                    placeholder="https://example.com"
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="password">{t('signup.password')}</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className={`signup-input ${fieldErrors.password ? 'input-error' : ''}`}
                  value={formData.password}
                  onChange={onChange}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle-btn"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {fieldErrors.password && <span className="error-text">{fieldErrors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('signup.confirmPassword')}</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  className={`signup-input ${fieldErrors.confirmPassword ? 'input-error' : ''}`}
                  value={formData.confirmPassword}
                  onChange={onChange}
                  required
                />
              </div>
              {fieldErrors.confirmPassword && <span className="error-text">{fieldErrors.confirmPassword}</span>}
            </div>

            {formData.roleName === 'INVESTIGATOR' && (
              <div className="orcid-info-msg" style={{
                background: 'rgba(166, 206, 57, 0.1)',
                border: '1px solid #a6ce39',
                padding: '10px',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#a6ce39',
                marginBottom: '1rem'
              }}>
                <FaOrcid style={{ marginRight: '8px' }} />
                {t('signup.orcidVerificationMsg')}
              </div>
            )}

            <div className="terms-group">
              <input
                type="checkbox"
                name="terms"
                id="terms-check"
                checked={formData.terms}
                onChange={onChange}
                required
              />
              <label htmlFor="terms-check" className="terms-text">
                {t('signup.terms')} <span className="terms-link">{t('signup.termsLink')}</span>
              </label>
              {fieldErrors.terms && <span className="error-text">{fieldErrors.terms}</span>}
            </div>

            <button type="submit" className="btn-signup-submit" disabled={loading}>
              {loading ? '...' : t('auth.registerBtn')}
            </button>
          </form>

          {/* 
          <div className="social-separator">{t('auth.socialLogin')}</div>

          <div className="social-buttons">
            <button className="btn-social google" onClick={() => handleSocialRegister('google')}>
              <FaGoogle /> Google
            </button>
            <button className="btn-social facebook" onClick={() => handleSocialRegister('facebook')}>
              <FaFacebook /> Facebook
            </button>
            {(formData.roleName === 'INVESTIGATOR' || formData.roleName === 'HEALTH_PROFESSIONAL') && (
              <button className="btn-social linkedin" onClick={() => handleSocialRegister('linkedin')}>
                <FaLinkedinIn /> LinkedIn
              </button>
            )}
            {formData.roleName === 'INVESTIGATOR' && (
              <button className="btn-social orcid" onClick={() => handleSocialRegister('orcid')}>
                <FaOrcid /> ORCID
              </button>
            )}
          </div>
          */}

          <div className="login-redirect">
            {t('auth.alreadyHaveAccount')}{' '}
            <SafeLink to="/login" style={{ color: '#FF6600', fontWeight: 'bold' }}>{t('login')}</SafeLink>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;